import { ConsentOptionResponse, Keys, LogoutReason } from '@eih-netbroker/common';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ReactComponent as ArrowDown } from '../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/checkbox-circle-fill.svg';
import { ReactComponent as LinkIcon } from '../../../assets/icons/external-link-line.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../context/SnackbarContext';
import { NbButton } from '../../../shared/components/button/Button';
import { NbLoader } from '../../../shared/components/loader/Loader';
import { NbTooltip } from '../../../shared/components/tooltip/Tooltip';
import { useLogout } from '../../../shared/services/authorization/useLogout';
import { useConsents } from '../../../shared/services/useConsents';
import { Consent } from '../pages/HomePage';
import classes from './FirstLoginDisclaimerModal.module.scss';

export interface FirstLoginDisclaimerModalProps {
  onCancel: (res: boolean | undefined) => void;
}

function getDisclaimerLink(disclaimer: ConsentOptionResponse) {
  if (!disclaimer.linkUrl) {
    throw new Error('Link URL is missing from the disclaimer');
  }
  return disclaimer.linkUrl;
}

export const FirstLoginDisclaimerModal = ({ onCancel }: FirstLoginDisclaimerModalProps) => {
  const { translation } = useLocale();
  const { showError } = useSnackbar();
  const { getConsents, updateConsent } = useConsents();
  const [firstConsents, setFirstConsents] = useState<Consent>(
    getConsents().find(consent => consent.consentId === Keys.Consents.FirstLogin)!,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [showScrollDownArrow, setShowScrollDownArrow] = useState(true);
  const contentRef: any = useRef(undefined);
  const textRef: any = useRef(undefined);
  const { logout } = useLogout();

  const shouldShowScrollDown = useCallback(() => {
    if (contentRef.current.scrollTop + contentRef.current.clientHeight < textRef.current.clientHeight) {
      setShowScrollDownArrow(true);
    } else {
      setShowScrollDownArrow(false);
    }
  }, []);

  const handleConsentOpened = (acceptedOptionId: string) => {
    const updatedFirstLoginConsentList = firstConsents?.options?.map(item => {
      if (item.optionId === acceptedOptionId) {
        return { ...item, accepted: true };
      }
      return item;
    });

    if (firstConsents && updatedFirstLoginConsentList) {
      setFirstConsents({ ...firstConsents, options: updatedFirstLoginConsentList });
    }
  };

  const handleAcceptFirstLoginConsents = async () => {
    try {
      setIsLoading(true);
      if (firstConsents && firstConsents.options && firstConsents.options.every(item => item.accepted)) {
        await updateConsent(
          Keys.Consents.FirstLogin,
          firstConsents.options.map(option => option.optionId),
        );
      }
      onCancel(true);
    } catch (err) {
      showError(err);
      logout(LogoutReason.ErrorWhileSavingConsent);
      onCancel(false);
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }

    const element = contentRef.current;
    element.addEventListener('scroll', shouldShowScrollDown);

    return () => {
      element.removeEventListener('scroll', shouldShowScrollDown);
    };
  }, [shouldShowScrollDown]);

  return (
    <div className={classes.disclaimer}>
      <h4>{translation.firstTimeLoginModal.title}</h4>

      <div
        ref={contentRef}
        className={classes.disclaimerContent}
      >
        <p
          dangerouslySetInnerHTML={{ __html: translation.firstTimeLoginModal.description }}
          ref={textRef}
        ></p>

        {isLoading && (
          <NbLoader
            horizontalCenter
            verticalCenter
          />
        )}

        {!isLoading &&
          firstConsents &&
          firstConsents.options &&
          firstConsents.options.map((disclaimer, index) => (
            <NbTooltip
              title={translation.firstTimeLoginModal.tooltip}
              placement={'top'}
              key={index}
            >
              <div className={`${classes.disclaimerItem} ${disclaimer.accepted ? classes.disclaimerItemIsOpened : ''}`}>
                <a
                  href={getDisclaimerLink(disclaimer)}
                  target={'_blank'}
                  rel={'noreferrer noopener'}
                  onClick={() => {
                    handleConsentOpened(disclaimer.optionId);
                  }}
                >
                  {disclaimer.linkText} <LinkIcon />
                </a>

                {disclaimer.accepted && <CheckIcon />}
              </div>
            </NbTooltip>
          ))}

        <div
          className={`${classes.disclaimerContentMore} ${
            showScrollDownArrow ? classes.disclaimerContentMoreIsShown : classes.disclaimerContentMoreIsHidden
          } `}
        >
          <ArrowDown />
        </div>
      </div>

      <div className={classes.disclaimerFooter}>
        <NbButton
          disabled={firstConsents?.options?.some(item => !item.accepted)}
          onClick={handleAcceptFirstLoginConsents}
          size={'large'}
        >
          {translation.firstTimeLoginModal.button}
        </NbButton>
      </div>
    </div>
  );
};
